import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import ContentParser from 'src/ui/library/ContentParser';
import Spinner from 'src/ui/library/Spinner/Spinner';
import Button from 'src/ui/library/Button';
import intl from 'react-intl-universal';
import { Edit, Delete } from '@material-ui/icons';
import { ConfirmDialog } from 'src/ui/library/Dialog';
import { transformFormValues } from 'src/helpers';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import RelatedEntitiesLanding from './RelatedEntitiesLanding';
import { fetchRelatedDetail, upsertRelatedRecord, deleteRelatedRecord } from 'src/services/cms.service';
import { RELATIONSHIP_CONSTANTS, MARITAL_STATUS, END_DATE_STATUS, LOOKUP_ID } from 'src/constants/constants';
import _ from 'lodash';
import { fnIsDefaultOldView, fnRemoveEmployeeKey, fnRemoveEmployeeFormKey } from 'src/helpers/commonFunctions';

class RelatedEntitiesDetailModal extends React.Component {
  static propTypes = {
    /** Id of the top level record */
    topLevelRecordId: PropTypes.string.isRequired,
    /** Id of a related record */
    recordId: PropTypes.string.isRequired,
    /** Id of a parent record */
    parentRecordId: PropTypes.string,
    /** Entity set name of parent record*/
    parentEntitySetName: PropTypes.string.isRequired,
    /** Entity logical name of parent record */
    parentEntityName: PropTypes.string.isRequired,
    /** Relationship name - used in api calls */
    relationshipName: PropTypes.string.isRequired,
    /** Display name of the entity */
    displayName: PropTypes.string.isRequired,
    /** Area - used for labels*/
    area: PropTypes.string.isRequired,
    /** Entity name - used for labeling via intl */
    entityName: PropTypes.string,
    /** Additional props - used to get services and other props  */
    additionalProps: PropTypes.object.isRequired,
    /** Func reference to get additional props for additional levels of relationships.
     * For example: A table within a modal of a related record.
     */
    getAdditionalPropsForRelatedEntities: PropTypes.func,
    /** Handler for on close of the modal */
    onClose: PropTypes.func.isRequired,
    /** Render in create mode if true */
    isCreate: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    let get = fetchRelatedDetail;
    let upsert = upsertRelatedRecord;
    let del = deleteRelatedRecord;

    this.onEdit = this.onEdit.bind(this);

    const { additionalProps } = props;

    if (additionalProps) {
      get = additionalProps.get ? additionalProps.get : get;
      upsert = additionalProps.upsert ? additionalProps.upsert : upsert;
    }

    this.state = {
      recordId: props.recordId,
      error: null,
      isLoaded: false,
      data: [],
      isEditable: false,
      showEditModal: false,
      showDeleteModal: false,
      formData: null,
      isCreate: props.isCreate,
      validationError: false,
      get,
      upsert,
      del,
    };
  }

  componentDidMount = () => {
    const { recordId, entityName, isCreate } = this.props;
    const { get } = this.state;

    get({ entityId: recordId, entityName }).then(
      result => {
        if (isCreate) {
          this.handleData(result);
        } else {
          this.handleEditData(result, 1);
        }
      },
      error => {
        this.setState({
          isLoaded: true,
          error,
        });
      },
    );
  };

  componentWillUnmount() {
    fnRemoveEmployeeKey();
    fnRemoveEmployeeFormKey();
  }

  handleData(result) {
    const lookUps = _.get(result, 'lookUps.ey_relationshiptype');
    if (lookUps) {
      const modifiedLookUps = lookUps.filter(
        e =>
          e.lookupItemId !== null &&
          e.lookupItemId !== LOOKUP_ID.PARENT_ID &&
          e.lookupItemId !== LOOKUP_ID.MATERNAL_GRANDPARENT_ID &&
          e.lookupItemId !== LOOKUP_ID.PATERNAL_PARENT_ID,
      );
      _.set(result, 'lookUps.ey_relationshiptype', modifiedLookUps);
    }

    this.setState({
      isLoaded: true,
      data: result,
      recordId: result.entityId,
    });
  }

  handleEditData(result, type) {
    const pageElement = _.get(result, 'pageElements');
    let value = '';
    let modifiedLookUps = [];
    let pageObj = _.find(
      pageElement,
      e => e.attributeName !== null && e.attributeName.toLowerCase() === RELATIONSHIP_CONSTANTS.relationshiptype,
    );
    value = pageObj && pageObj.value;
    const lookUps = _.get(result, 'lookUps.ey_relationshiptype');
    if (value && lookUps) {
      const __getModifiedLookUps = (lookUps, ids) => {
        return _.filter(lookUps, e => {
          return e.lookupItemId !== null && !_.includes(ids, e.lookupItemId);
        });
      };
      switch (value) {
        case '' + LOOKUP_ID.PARENT_ID:
          modifiedLookUps = __getModifiedLookUps(lookUps, [LOOKUP_ID.MATERNAL_GRANDPARENT_ID, LOOKUP_ID.PATERNAL_PARENT_ID]);
          break;
        case '' + LOOKUP_ID.MATERNAL_GRANDPARENT_ID:
          modifiedLookUps = __getModifiedLookUps(lookUps, [LOOKUP_ID.PARENT_ID, LOOKUP_ID.PATERNAL_PARENT_ID]);
          break;
        case '' + LOOKUP_ID.PATERNAL_PARENT_ID:
          modifiedLookUps = __getModifiedLookUps(lookUps, [LOOKUP_ID.PARENT_ID, LOOKUP_ID.MATERNAL_GRANDPARENT_ID]);
          break;
        default:
          modifiedLookUps = lookUps.filter(
            e =>
              e.lookupItemId !== null &&
              e.lookupItemId !== LOOKUP_ID.PARENT_ID &&
              e.lookupItemId !== LOOKUP_ID.MATERNAL_GRANDPARENT_ID &&
              e.lookupItemId !== LOOKUP_ID.PATERNAL_PARENT_ID,
          );
          break;
      }
      _.set(result, 'lookUps.ey_relationshiptype', modifiedLookUps);
    }

    if (type === 1) {
      this.setState({
        isLoaded: true,
        data: result,
        recordId: result.entityId,
      });
    } else {
      this.setState({
        isLoaded: true,
        data: result,
        isEditable: false,
        isCreate: false,
        recordId: result.entityId,
      });
      if (this.props.entityName !== 'ey_contactemploymenthistory') {
        this.props.onClose();
      }
    }
  }

  onCancelEditModal = () => {
    this.setState({
      showEditModal: false,
      isEditable: false,
    });
  };

  onConfirmEditModal = () => {
    this.setState({
      showEditModal: false,
      isEditable: true,
    });
  };

  onCancel = () => {
    this.setState({
      showEditModal: true,
    });
  };

  onSave = () => {
    this.setState({
      isLoaded: false,
    });

    const { upsert, formData, recordId } = this.state;
    const { formValues } = formData;

    const { topLevelRecordId, parentRecordId, relationshipName, parentEntityName, entityName } = this.props;

    const data = {
      topLevelRecordId,
      entityLogicalName: entityName,
      relationshipName,
      parentId: parentRecordId,
      parentEntityName,
      entityId: recordId,
      ...transformFormValues(formValues),
    };

    upsert(data).then(
      result => {
        this.handleEditData(result, 2);
      },
      error => {
        this.setState({
          isLoaded: true,
          error,
          isEditable: false,
        });
        this.props.onClose();
      },
    );
  };

  onEdit = () => {
    this.setState({
      isEditable: true,
    });
  };

  fillPageElement(pageData, formData) {
    return pageData.data.pageElements.map(function(el) {
      if (formData.formValues.dataRequests[el.elementName] !== undefined || formData.formValues.dataRequests[el.elementName] !== '') {
        el.value = formData.formValues.dataRequests[el.elementName];
      }
      return el;
    });
  }

  handleFormChange = (pageData, formData) => {
    if (pageData.data != null)
      var ddlExpirationDateObj = pageData.data.pageElements.find(
        ele =>
          ele.attributeName === RELATIONSHIP_CONSTANTS.expiration || ele.attributeName === RELATIONSHIP_CONSTANTS.doesThisRecordHaveEndDate,
      );
    var ddlEndDateObj = pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate);
    var ddlEndDateValueObj = pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue);
    var MarritalStatus = pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus);
    var DateOfMarriageObj = pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage);
    var DateOfDivorceObj = pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce);
    if (ddlExpirationDateObj && ddlExpirationDateObj.elementName === formData.elementUpdated) {
      let value = formData.formValues.dataRequests[ddlExpirationDateObj.elementName];
      if (value === END_DATE_STATUS.yes) {
        if (formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate)) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).props.showMe = true;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).showMe = true;
        }
        if (formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue)) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).props.showMe = true;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).showMe = true;
        }
      } else if (value === END_DATE_STATUS.no) {
        if (formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate)) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).props.showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).value = '';
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).props.value = '';
          formData.formValues.dataRequests[ddlEndDateObj.elementName] = '';
          this.setState({ data: pageData.data, formData });
          pageData.data.pageElements = this.fillPageElement(pageData, formData);
        }
        if (formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue)) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).props.showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).value = '';
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).props.value = '';
          formData.formValues.dataRequests[ddlEndDateValueObj.elementName] = '';
          this.setState({ data: pageData.data, formData });
          pageData.data.pageElements = this.fillPageElement(pageData, formData);
        }
      }
      let stateObj = { data: pageData.data, formData, isLoaded: false, setExpirationField: [] };
      if (pageData.isCreate) {
        stateObj.isCreate = false;
        stateObj.setExpirationField.push('isCreate');
      } else if (pageData.isEditable) {
        stateObj.isEditable = false;
        stateObj.setExpirationField.push('isEditable');
        stateObj.isCreate = false;
        stateObj.setExpirationField.push('isCreate');
      }
      this.setState(stateObj);
    }
    if (MarritalStatus && MarritalStatus.elementName === formData.elementUpdated) {
      if (
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value ===
        MARITAL_STATUS.married
      ) {
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).props.showMe = true;
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).showMe = true;
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).props.showMe = false;
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).showMe = false;
      }
      if (
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value ===
        MARITAL_STATUS.divorcee
      ) {
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).props.showMe = false;
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).showMe = false;
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).props.showMe = true;
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).showMe = true;
      }
      if (
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value !==
        MARITAL_STATUS.married
      ) {
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).props.showMe = false;
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).showMe = false;
        formData.formValues.dataRequests[DateOfMarriageObj.elementName] = '';
      }
      if (
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value !==
        MARITAL_STATUS.divorcee
      ) {
        formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).props.showMe = false;
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).showMe = false;
        formData.formValues.dataRequests[DateOfDivorceObj.elementName] = '';
      }
      let stateObj = { data: pageData.data, formData, isLoaded: false, setExpirationField: [] };
      if (pageData.isCreate) {
        stateObj.isCreate = false;
        stateObj.setExpirationField.push('isCreate');
      } else if (pageData.isEditable) {
        stateObj.isEditable = false;
        stateObj.setExpirationField.push('isEditable');
        stateObj.isCreate = false;
        stateObj.setExpirationField.push('isCreate');
      }
      this.setState(stateObj);
    } else {
      if (
        pageData &&
        pageData.data &&
        pageData.data.pageElements &&
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate) != null
      ) {
        if (ddlExpirationDateObj && formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate)) {
          if (formData.formValues.dataRequests[ddlExpirationDateObj.elementName] === END_DATE_STATUS.yes) {
            formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).props.showMe = true;
            pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).showMe = true;
          }
          if (formData.formValues.dataRequests[ddlExpirationDateObj.elementName] === END_DATE_STATUS.no) {
            formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).props.showMe = false;
            pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDate).showMe = false;
          }
        }

        if (
          ddlExpirationDateObj &&
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue)
        ) {
          if (formData.formValues.dataRequests[ddlExpirationDateObj.elementName] === END_DATE_STATUS.yes) {
            formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).props.showMe = true;
            pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.endDateValue).showMe = true;
          }
        }
      }
      if (
        pageData &&
        pageData.data &&
        pageData.data.pageElements &&
        pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus) != null
      ) {
        if (
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value ===
          MARITAL_STATUS.married
        ) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).props.showMe = true;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).showMe = true;
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).props.showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).showMe = false;
        }
        if (
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value ===
          MARITAL_STATUS.divorcee
        ) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).props.showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).showMe = false;
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).props.showMe = true;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).showMe = true;
        }
        if (
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value !==
          MARITAL_STATUS.married
        ) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).props.showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfMarriage).showMe = false;
        }
        if (
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.maritalStatus).props.value !==
          MARITAL_STATUS.divorcee
        ) {
          formData.formValues.rawElements.find(ele => ele.attributeName === RELATIONSHIP_CONSTANTS.dateOfDivorce).props.showMe = false;
          pageData.data.pageElements.find(ele => ele.attributeName === 'ey_dateofdivorce').showMe = false;
        }
      }
      this.setState({ data: pageData.data, formData });
      pageData.data.pageElements = this.fillPageElement(pageData, formData);
    }
    if (formData.elementUpdated) {
      pageData.data.pageElements = this.fillPageElement(pageData, formData);
    }
  };
  componentDidUpdate() {
    if (this.state.setExpirationField && this.state.setExpirationField.length > 0) {
      let stateObj = { isLoaded: true, setExpirationField: [] };
      for (let index = 0; index < this.state.setExpirationField.length; index++) {
        stateObj[this.state.setExpirationField[index]] = true;
      }
      this.setState(stateObj);
    }
  }

  onDelete = () => {
    this.setState({
      showDeleteModal: true,
    });
  };

  onCancelDeleteModal = () => {
    this.setState({
      showDeleteModal: false,
    });
  };

  onConfirmDeleteModal = () => {
    const { onClose, entityName } = this.props;
    const { del, recordId } = this.state;
    this.setState({
      showDeleteModal: false,
    });

    del({ entityId: recordId, entityName }).then(
      res => {
        onClose();
      },
      error => {
        onClose();
      },
    );
  };

  render() {
    const { onClose, readOnly, topLevelRecordId, area, additionalProps, getAdditionalPropsForRelatedEntities, entityName } = this.props;
    let { displayName } = this.props;
    const { onValidate, relatedEntities } = additionalProps;
    const { isLoaded, error, data, isEditable, showEditModal, showDeleteModal, isCreate, formData, validationError, recordId } = this.state;
    displayName = displayName || additionalProps.displayName;
    let title = `${isCreate ? 'New ' : ''}${displayName || 'Record'}`;

    return (
      <Dialog
        open
        onClose={onClose}
        aria-labelledby="dialog-title"
        fullWidth
        maxWidth="lg"
        style={{
          marginTop: '1.5rem',
        }}
        className={fnIsDefaultOldView() ? 'largemodal' : 'largemodal add-details-modal'}>
        {!isLoaded && (
          <div style={{ marginTop: '10%', marginBottom: '10%' }}>
            <Spinner size={50} />
          </div>
        )}

        {isLoaded && (
          <Fragment>
            <DialogTitle id="dialog-title">
              {title}
              <IconButton aria-label="close" onClick={onClose} className="close-button">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {error && <div>Error: {error.message}</div>}
              {isLoaded && !error && (
                <div className="content">
                  {showEditModal && (
                    <ConfirmDialog
                      open
                      title={intl.get(`${area}.details.confirmDialogTitle`)}
                      cancelText={intl.get(`${area}.details.buttons.cancelEdit`)}
                      confirmText={intl.get(`${area}.details.buttons.continueEdit`)}
                      onCancel={this.onCancelEditModal}
                      onConfirm={this.onConfirmEditModal}>
                      {intl.get(`${area}.details.cancelEditDialogMessage`)}
                    </ConfirmDialog>
                  )}
                  {showDeleteModal && (
                    <ConfirmDialog
                      open
                      title={intl.get(`${area}.details.confirmDialogTitle`)}
                      cancelText={intl.get(`${area}.details.buttons.cancel`)}
                      confirmText={intl.get(`${area}.details.buttons.confirm`)}
                      onCancel={this.onCancelDeleteModal}
                      onConfirm={this.onConfirmDeleteModal}>
                      {intl.get(`${area}.details.deleteRecordDialogMessage`)}
                    </ConfirmDialog>
                  )}
                  <ContentParser
                    key={`${isEditable}-${recordId}`}
                    data={data}
                    readOnly={!isCreate && !isEditable}
                    showLabels={false}
                    numberOfColumns={4}
                    debug={false}
                    onValidate={(fields, setErrorFn) => {
                      this.setState({
                        validationError: onValidate ? onValidate(fields, setErrorFn) : false,
                      });
                    }}
                    onChange={item => {
                      this.handleFormChange(this.state, item);
                    }}
                  />
                  {!isCreate &&
                    !isEditable &&
                    relatedEntities &&
                    relatedEntities.relationships &&
                    relatedEntities.relationships.length > 0 &&
                    relatedEntities.relationships.map(relationshipName => {
                      const additionalPropsForRelated = getAdditionalPropsForRelatedEntities(relationshipName);
                      return (
                        <Fragment>
                          <h3 class="related-entities-title">{additionalPropsForRelated.tabLabel}</h3>
                          <RelatedEntitiesLanding
                            key={relationshipName}
                            topLevelRecordId={topLevelRecordId}
                            parentRecordId={recordId}
                            parentEntitySetName={relatedEntities.parentEntitySetName}
                            parentEntityName={relatedEntities.parentEntityName}
                            relationshipName={relationshipName}
                            additionalProps={additionalPropsForRelated}
                            getAdditionalPropsForRelatedEntities={getAdditionalPropsForRelatedEntities}
                            area={area}
                          />
                        </Fragment>
                      );
                    })}
                </div>
              )}
            </DialogContent>
            {!readOnly && formData && (
              <DialogActions>
                <section className="actions">
                  {isCreate || isEditable ? (
                    <div className="save-cancel-wrapper">
                      <Button styleType="secondary" onClick={isCreate ? onClose : this.onCancel} className="action-button">
                        {intl.get(`${area}.details.buttons.cancel`)}
                      </Button>
                      <Button className="action-button" disabled={!formData.isFormComplete || validationError} onClick={this.onSave}>
                        {intl.get(`${area}.details.buttons.save`)}
                      </Button>
                    </div>
                  ) : (
                    <Fragment>
                      <div className="edit-delete-wrapper">
                        <a className="link" onClick={this.onEdit}>
                          <Edit className="icon" /> <span className="text">{intl.get(`${area}.details.buttons.edit`)}</span>
                        </a>
                        {entityName === 'ey_contactemploymenthistory' && (
                          <a className="link" onClick={this.onDelete}>
                            <Delete className="icon" /> <span className="text">{intl.get(`${area}.details.buttons.delete`)}</span>
                          </a>
                        )}
                      </div>
                      <div className="edit-delete-wrapper right-end">
                        {entityName !== 'ey_contactemploymenthistory' ? (
                          <a className="link" onClick={this.onDelete}>
                            <Delete className="icon" /> <span className="text">{intl.get(`${area}.details.buttons.delete`)}</span>
                          </a>
                        ) : (
                          <Button className="action-button" onClick={onClose}>
                            {intl.get(`${area}.details.buttons.save`)}
                          </Button>
                        )}
                      </div>
                    </Fragment>
                  )}
                </section>
              </DialogActions>
            )}
          </Fragment>
        )}
      </Dialog>
    );
  }
}

const mapStateToProps = () => ({});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(RelatedEntitiesDetailModal);
